import { Drawer, Modal } from "antd";
import classNames from "classnames";
import cuid from "cuid";
import dayjs from "dayjs";
import { useFormik } from "formik";
import { get, isEmpty } from "lodash";
import omitDeep from "omit-deep-lodash";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaMinus, FaPlus } from "react-icons/fa";
import { MdOutlineRadioButtonChecked } from "react-icons/md";
import { RxCross2 } from "react-icons/rx";
import { useMediaQuery } from "react-responsive";
import Help from "shared/components/SVG/LMF/Help";
import HelpBlue from "shared/components/SVG/LMF/HelpBlue";
import { NAME_SPACES } from "shared/locales/constants";
import "swiper/css";
import "swiper/css/grid";
import "swiper/css/navigation";
import "swiper/css/pagination";
import "swiper/css/scrollbar";
import { Navigation } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import {
  ATTACHMENT_TYPES,
  DATE_FORMAT,
  RELATIONSHIP,
  SUPPORTING_INFORMATION_CATEGORIES,
} from "utils/constants";
import { hundredYearsAgoStart } from "utils/helpers/date";
import { downloadDocument } from "utils/helpers/files";
import { bindDateInputProps } from "utils/helpers/input";
import ActionButtons from "../Components/ActionButtons";
import Button from "../Components/Button";
import CustomModal from "../Components/CustomModal";
import Head from "../Components/Head";
import InfoBox from "../Components/InfoBox";
import Input from "../Components/Input";
import Layout from "../Components/Layout";
import ProductCard from "../Components/PricingCard";
import checkMarkSmall from "../Components/svg/check_small-secondary.svg";
import relatedPersonIcon from "../Components/svg/related-person-icon.svg";
import flagshipGuaranteesItems from "./flagshipGuarantees.json";
import validation from "./validation";

const View = ({
  onSendQuote,
  products,
  contact,
  refetchCosts,
  refetchLoading,
  downloadSingleDocument,
  locked,
  duplicateProject,
  getInTouch,
  onBack,
}) => {
  const [benefits, setBenefits] = useState([]);
  const { t } = useTranslation(NAME_SPACES.PUBLIC.PROCESSES);
  const TRANSLATION = t("DETAIL", { returnObjects: true });
  const TRANSLATION_2 = t("B2C_LMF.PRODUCTS", { returnObjects: true });
  const [openEditModal, setOpenEditModal] = useState(false);
  const [openInfoModal, setOpenInfoModal] = useState(false);
  const [open, setOpen] = useState(false);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const onSubmit = async ({ spouse, children }) => {
    await refetchCosts({
      contact: {
        id: contact.id,
        relatedPersons: [spouse, ...children],
      },
    });
    setAddBeneficiaries((prev) => ({ ...prev, visible: false }));
  };

  const flagshipGuarantees = (insurancePlan) => {
    const level =
      get(insurancePlan, "level") ||
      get(insurancePlan, "name", " ").split(" ").pop();
    const name = get(insurancePlan, "name", "").replace(/\s\d+$/, "");
    if (name.toLowerCase().indexOf("mutuelle de commune") !== -1)
      return (get(flagshipGuaranteesItems, `MDC.${level}`, []) || [])?.map(
        (coverage) => ({
          mainInfo: coverage,
          limit: "",
          complement: "",
        }),
      );
    return get(
      flagshipGuaranteesItems,
      `${name}.${level}`,
      flagshipGuaranteesItems.MDC[level || "1"] || [],
    )?.map((coverage) => ({
      mainInfo: coverage,
      limit: "",
      complement: "",
    }));
  };

  const onDownloadDocument = ({ type, insurancePlanId }) => {
    downloadSingleDocument({
      variables: {
        where: {
          insurancePlans: { id: insurancePlanId },
          type,
        },
      },
      onCompleted: (_) => {
        const { document } = omitDeep(_.downloadSingleDocument, "__typename");
        if (!document) return;
        downloadDocument(document);
      },
    });
  };

  const { handleSubmit, values, ...formProps } = useFormik({
    initialValues: {
      spouse: get(contact, "relatedPersons", []).find(
        ({ relationship }) => relationship === RELATIONSHIP.SPOUSE,
      ),
      children: get(contact, "relatedPersons", []).filter(
        ({ relationship }) => relationship === RELATIONSHIP.CHILD,
      ),
    },
    onSubmit,
    validationSchema: validation(
      t(`${NAME_SPACES.ERRORS}:VALIDATIONS`, { returnObjects: true }),
    ),
  });
  const [childrenNumber, setChildrenNumber] = useState(
    get(values, "children", []).length,
  );

  const [addBeneficiaries, setAddBeneficiaries] = useState({
    visible: false,
    spouse: !isEmpty(get(values, "spouse")),
    children: !isEmpty(get(values, "children")),
    status: !(
      isEmpty(get(values, "spouse")) && isEmpty(get(values, "children"))
    ),
  });
  const [disabled, setDisabled] = useState({});

  useEffect(() => {
    const children = get(values, "children", []);
    const oldChildrenLength = children.length;

    if (oldChildrenLength < childrenNumber) {
      formProps.setFieldValue("children", [
        ...children,
        {
          id: cuid(),
          relationship: RELATIONSHIP.CHILD,
          identity: {
            id: cuid(),
            user: {
              id: cuid(),
              birthDate: null,
            },
          },
        },
      ]);
    } else if (oldChildrenLength > childrenNumber) {
      formProps.setFieldValue("children", children.slice(0, childrenNumber));
    }
  }, [childrenNumber]);

  const onOkClick = async () => {
    await duplicateProject();
    // setOpenEditModal(false);
  };

  const onHelpClick = (benefits) => {
    setBenefits(benefits);
    setOpen(true);
  };

  const handleYesClick = () => {
    // if (locked) return setOpenEditModal(true);
    setAddBeneficiaries((prev) => ({
      ...prev,
      status: true,
    }));

    if (isMobile) {
      setDrawerVisible(true);
    } else {
      setAddBeneficiaries((prev) => ({
        ...prev,
        visible: true,
      }));
    }
  };

  const handleNoClick = () => {
    if (!addBeneficiaries?.status) return;
    // if (locked) return setOpenEditModal(true);

    formProps.setFieldValue("spouse", undefined);
    formProps.setFieldValue("children", []);
    setChildrenNumber(0);
    setAddBeneficiaries({
      spouse: false,
      children: false,
      visible: false,
      status: false,
    });
    handleSubmit();
  };

  const isMobile = useMediaQuery({ maxWidth: 768 });

  return (
    <>
      <CustomModal
        open={open}
        setOpen={setOpen}
        title={TRANSLATION_2.HELP_MODAL.FORMULA}
        TRANSLATION_2={TRANSLATION_2}
        width={420}
        closeText={TRANSLATION_2.HELP_MODAL.CLOSE}
      >
        {benefits.length && (
          <ul>
            {benefits.map(({ coverage, limit, complement }, index) => (
              <li key={`benefit-${index}`}>
                <img src={checkMarkSmall} alt="" />
                <span>
                  {coverage} <span>{limit}</span> <sup>{complement}</sup>
                </span>
              </li>
            ))}
          </ul>
        )}
      </CustomModal>

      <Modal
        type="info"
        open={openEditModal}
        message={TRANSLATION.LOCKED_EDIT_MODAL.MESSAGE}
        okText={TRANSLATION.LOCKED_EDIT_MODAL.OK}
        cancelText={TRANSLATION.LOCKED_EDIT_MODAL.CANCEL}
        onOk={onOkClick}
        onCancel={() => setOpenEditModal(false)}
      />

      <CustomModal
        open={openInfoModal}
        setOpen={setOpenInfoModal}
        title={TRANSLATION_2.HELP_MODAL.READ_MORE}
        description={TRANSLATION_2.HELP_MODAL.DESCRIPTION}
        TRANSLATION_2={TRANSLATION_2}
        width={420}
        closeText={TRANSLATION_2.HELP_MODAL.CLOSE}
      >
        <ul className="lmf__list">
          <li>
            <MdOutlineRadioButtonChecked size={16} color="#ff9d00" />
            <span>{TRANSLATION_2.HELP_MODAL.OPTIONS.OPT_1}</span>
          </li>
          <li>
            <MdOutlineRadioButtonChecked size={16} color="#ff9d00" />
            <span>{TRANSLATION_2.HELP_MODAL.OPTIONS.OPT_2}</span>
          </li>
          <li>
            <MdOutlineRadioButtonChecked size={16} color="#ff9d00" />
            <span>{TRANSLATION_2.HELP_MODAL.OPTIONS.OPT_3}</span>
          </li>
          <li>
            <MdOutlineRadioButtonChecked size={16} color="#ff9d00" />
            <span>{TRANSLATION_2.HELP_MODAL.OPTIONS.OPT_4}</span>
          </li>
        </ul>
      </CustomModal>
      <Layout>
        <Head
          description={TRANSLATION_2.DESCRIPTION}
          title={TRANSLATION_2.TITLE}
          onBack={onBack}
        />

        <div className="lmfProduct__wrapper">
          <div className="lmfcontent__related_persons lmf__form">
            <div className="beneficiary__lmf LMF-content">
              <div
                className="beneficiary__lmf--main"
                {...(isMobile && {
                  onClick: () => {
                    if (isMobile) setDrawerVisible(true);
                    else
                      setAddBeneficiaries((prev) => ({
                        ...prev,
                        visible: !prev.visible,
                      }));
                  },
                })}
              >
                <div className="beneficiary__lmf--main--left">
                  <span className="question">
                    <img src={relatedPersonIcon} alt="" />
                    {isMobile
                      ? addBeneficiaries.spouse || childrenNumber > 0
                        ? TRANSLATION.PRODUCTS.YOUR_BENEFICIARIES
                        : TRANSLATION.PRODUCTS.ADD_BENEFICIARY
                      : TRANSLATION.PRODUCTS.ADD_BENEFICIARY}
                  </span>
                </div>

                {isMobile &&
                  (addBeneficiaries.spouse || childrenNumber > 0) && (
                    <span className="lmfselected--beneficiaries">
                      {addBeneficiaries.spouse && childrenNumber > 0
                        ? "Conjoint et " +
                          childrenNumber +
                          " enfant" +
                          (childrenNumber > 1 ? "s" : "")
                        : addBeneficiaries.spouse
                          ? "Conjoint"
                          : childrenNumber > 0
                            ? childrenNumber +
                              " enfant" +
                              (childrenNumber > 1 ? "s" : "")
                            : ""}
                    </span>
                  )}

                {!isMobile && (
                  <div className="d-flex">
                    <Button
                      type={classNames({
                        primary: addBeneficiaries?.status,
                        white: !addBeneficiaries?.status,
                      })}
                      size="medium"
                      onClick={handleYesClick}
                    >
                      {TRANSLATION.YES}
                    </Button>

                    <Button
                      type={classNames({
                        primary: !addBeneficiaries?.status,
                        white: addBeneficiaries?.status,
                      })}
                      size="medium"
                      onClick={handleNoClick}
                    >
                      {TRANSLATION.NO}
                    </Button>
                  </div>
                )}
              </div>

              <div
                className={classNames("beneficiary__lmf--expand", {
                  hidden: !addBeneficiaries.visible,
                })}
              >
                {!isMobile && (
                  <>
                    <InfoBox
                      variant="warning"
                      text={TRANSLATION_2.INFO_WARNING}
                      icon={<Help size={48} />}
                    />

                    <div className="beneficiary__lmf--expand--row spouse">
                      <Input
                        type="checkbox"
                        label={TRANSLATION.PRODUCTS.SPOUSE}
                        checked={addBeneficiaries.spouse}
                        onChange={({ target: { checked } }) => {
                          setAddBeneficiaries((prev) => ({
                            ...prev,
                            spouse: checked,
                          }));
                          if (checked)
                            formProps.setFieldValue("spouse", {
                              id: cuid(),
                              relationship: RELATIONSHIP.SPOUSE,
                              identity: {
                                id: cuid(),
                                user: {
                                  id: cuid(),
                                  birthDate: null,
                                },
                              },
                            });
                          else formProps.setFieldValue("spouse", undefined);
                        }}
                      />
                      <div className="field">
                        <Input
                          type="date"
                          placeholder={TRANSLATION.BIRTH_DATE.LABEL}
                          format={DATE_FORMAT}
                          disabled={!addBeneficiaries.spouse}
                          showToday={false}
                          {...bindDateInputProps({
                            name: "spouse.identity.user.birthDate",
                            values,
                            ...formProps,
                          })}
                          defaultPickerValue={
                            !get(values, `spouse.identity.user.birthDate`) &&
                            dayjs().subtract(24, "years")
                          }
                          disabledDate={(current) =>
                            current >
                              dayjs().subtract(18, "years").endOf("year") ||
                            current < hundredYearsAgoStart
                          }
                        />
                      </div>
                    </div>
                    <div className="divider" />
                    <div className="beneficiary__lmf--expand--row">
                      <div className="beneficiary__lmf--expand--row__children">
                        <Input
                          type="checkbox"
                          label={TRANSLATION.PRODUCTS.CHILDREN}
                          checked={addBeneficiaries.children}
                          onChange={({ target: { checked } }) => {
                            if (!checked) {
                              setChildrenNumber(0);
                              formProps.setFieldValue("children", []);
                            } else if (childrenNumber === 0)
                              setChildrenNumber(1);
                            setAddBeneficiaries((prev) => ({
                              ...prev,
                              children: checked,
                            }));
                          }}
                        />
                        <p>Gratuité à partir du 3ème enfant </p>
                      </div>
                      <div className="children-count">
                        <Button
                          className="button-small-rounded"
                          onClick={() => setChildrenNumber((prev) => +prev - 1)}
                          disabled={
                            childrenNumber < 2 || !addBeneficiaries.children
                          }
                        >
                          <FaMinus size={14} />
                        </Button>
                        <div className="flex--grow-1 counter">
                          <Input
                            type="number"
                            min={0}
                            max={12}
                            placeholder={"0"}
                            name={"gender"}
                            value={childrenNumber}
                            onChange={({ target: { value } }) => {
                              setChildrenNumber(+value);
                            }}
                            disabled={!addBeneficiaries.children}
                          />
                        </div>
                        <Button
                          className="button-small-rounded"
                          onClick={() => setChildrenNumber((prev) => +prev + 1)}
                          disabled={
                            childrenNumber > 11 || !addBeneficiaries.children
                          }
                        >
                          <FaPlus size={14} />
                        </Button>
                      </div>
                    </div>
                    <div className="children-birthday">
                      {childrenNumber > 0 && (
                        <InfoBox
                          variant="info"
                          text={TRANSLATION_2.INFO_INFO}
                          icon={<HelpBlue size={40} />}
                          onClick={() => setOpenInfoModal(true)}
                        />
                      )}

                      {Array(childrenNumber)
                        .fill()
                        .map((_, i) => (
                          <Input
                            direction="row"
                            key={`child-${i}`}
                            label={`${
                              TRANSLATION.NUMBERS_MAPPING[i + 1] || i + 1
                            } ${
                              TRANSLATION.CHILDREN_BIRTHDATES.CHILD
                            }  (-28 ans)`}
                            placeholder={
                              TRANSLATION.CHILDREN_BIRTHDATES.BIRTH_DATE
                            }
                            type="date"
                            variant="simple"
                            format={DATE_FORMAT}
                            disabledDate={(current) => {
                              return (
                                current > dayjs() ||
                                current <
                                  dayjs().subtract(28, "year").startOf("year")
                              );
                            }}
                            defaultPickerValue={
                              !get(
                                values,
                                `children.${i}.identity.user.birthDate`,
                              ) && dayjs().subtract(1, "years")
                            }
                            showToday={false}
                            {...bindDateInputProps({
                              name: `children.${i}.identity.user.birthDate`,
                              values,
                              ...formProps,
                            })}
                          />
                        ))}
                    </div>
                    <Button
                      type="white"
                      size="middle"
                      className="w-full"
                      onClick={() => handleSubmit()}
                      disabled={
                        !isEmpty(formProps.errors) ||
                        (childrenNumber === 0 && !addBeneficiaries.spouse)
                      }
                    >
                      {TRANSLATION.PRODUCTS.SUBMIT}
                    </Button>
                  </>
                )}
              </div>
            </div>
            {(childrenNumber > 0 || addBeneficiaries.spouse) && (
              <div className="beneficiary_chips">
                {!isMobile && (
                  <>
                    {addBeneficiaries.spouse &&
                      get(values, "spouse.identity.user.birthDate") && (
                        <div className="beneficiary_chips--button">
                          <span>{TRANSLATION.PRODUCTS.SPOUSE}</span>
                          <RxCross2
                            size={14}
                            onClick={() => {
                              formProps.setFieldValue("spouse", undefined);
                              setAddBeneficiaries((prev) => ({
                                ...prev,
                                spouse: false,
                              }));
                              handleSubmit();
                            }}
                          />
                        </div>
                      )}
                    {get(values, "children", [])
                      .filter((child) => get(child, "identity.user.birthDate"))
                      .map((_, i) => (
                        <div
                          key={`child_${i}`}
                          className="beneficiary_chips--button"
                        >
                          <span>{`${TRANSLATION.CHILDREN.CHILD.TITLE} ${
                            i + 1
                          }`}</span>
                          <RxCross2
                            onClick={() => {
                              values.children.splice(i, 1);
                              setChildrenNumber((prev) => +prev - 1);
                              if (childrenNumber === 1)
                                setAddBeneficiaries((prev) => ({
                                  ...prev,
                                  children: false,
                                }));
                              formProps.setFieldValue(
                                "children",
                                get(values, "children", []),
                              );
                              handleSubmit();
                            }}
                            size={14}
                          />
                        </div>
                      ))}
                  </>
                )}
              </div>
            )}
          </div>

          <div className="lmf__swiper">
            <Swiper
              modules={[Navigation]}
              slidesPerView={"auto"}
              spaceBetween={8}
              autoplay={{
                delay: 8000,
                disableOnInteraction: false,
              }}
              navigation={{
                nextEl: ".swiper-button-next",
                prevEl: ".swiper-button-prev",
              }}
              pagination={{
                el: ".swiper-pagination",
                clickable: true,
              }}
              breakpoints={{
                100: {
                  slidesPerView: 1,
                  centeredSlides: true,
                  centerInsufficientSlides: true,
                },
                320: {
                  slidesPerView: 1,
                  centeredSlides: true,
                  centerInsufficientSlides: true,
                },
                520: {
                  slidesPerView: 1,
                  centeredSlides: true,
                  centerInsufficientSlides: true,
                },
                640: {
                  slidesPerView: 2,
                  centeredSlides: false,
                  centerInsufficientSlides: false,
                },
                1024: {
                  slidesPerView: 3,
                  centeredSlides: false,
                  centerInsufficientSlides: false,
                },
              }}
            >
              {products.map(({ id, cost, currency, insurancePlan }, i) => {
                const supportingInfo = get(
                  insurancePlan,
                  "supportingInformation",
                  [],
                ).find(
                  (item) =>
                    item.category ===
                    SUPPORTING_INFORMATION_CATEGORIES.HEADLIGHT_WARRANTY,
                );
                return (
                  <SwiperSlide key={insurancePlan.id}>
                    <ProductCard
                      perMonth={true}
                      price={{
                        cost,
                        currency,
                        period: TRANSLATION.PRODUCTS.PER_MONTH,
                      }}
                      planName={get(insurancePlan, "name")}
                      // onHelpClick={() =>
                      //   onHelpClick(get(insurancePlan, `fields.flagshipGuarantees`))
                      // }
                      action={{
                        onClick: () => {
                          setDisabled({ [id]: true });
                          onSendQuote(insurancePlan.id);
                        },
                        label: TRANSLATION_2.PRODUCTS.CHOOSE_OFFER,
                        loading: disabled[id] || refetchLoading,
                        disabled:
                          !isEmpty(disabled) || addBeneficiaries.visible,
                      }}
                      info={{
                        title: "Les garanties phares",
                        items: Array.isArray(supportingInfo?.value)
                          ? supportingInfo.value
                          : flagshipGuarantees(insurancePlan),
                      }}
                      links={[
                        ...get(insurancePlan, "fields.externalLinks", []).map(
                          (link) => ({
                            onClick: () => {
                              let url = link?.value;
                              if (url && !url.startsWith("https://")) {
                                url = "https://" + url;
                              }
                              window.open(url, "_blank");
                            },
                            label: link?.text,
                          }),
                        ),
                        {
                          onClick: () =>
                            onDownloadDocument({
                              type: ATTACHMENT_TYPES.BROCHURE,
                              insurancePlanId: insurancePlan.id,
                            }),
                          label: TRANSLATION.PRODUCTS.TABLE_GARANTIES,
                        },
                        {
                          onClick: () =>
                            onDownloadDocument({
                              type: ATTACHMENT_TYPES.IPID,
                              insurancePlanId: insurancePlan.id,
                            }),
                          label: TRANSLATION.PRODUCTS.IPID,
                        },
                      ]}
                      isRecommended={i === 1}
                      translation={TRANSLATION.PRODUCTS}
                    />
                  </SwiperSlide>
                );
              })}
            </Swiper>
            <div className="lmf_swiper__arrows">
              <div className="swiper-button-prev"></div>
              <div className="swiper-button-next"></div>
            </div>
            <div className="swiper-pagination" />
          </div>
        </div>

        <div className="lmfCustom_offer lmf__form">
          <div className="lmfCustom_offer--info">
            <span className="lmfCustom_offer--info__title">
              {TRANSLATION_2.GET_IN_TOUCH.TITLE}
            </span>
            <span className="lmfCustom_offer--info__description">
              {TRANSLATION_2.GET_IN_TOUCH.DESCRIPTION}
            </span>
          </div>
          <Button type="white" size="medium" onClick={getInTouch}>
            {TRANSLATION_2.GET_IN_TOUCH.ACTION}
          </Button>
        </div>

        <ActionButtons
          {...(isMobile && { onBack })}
          className={classNames({
            "w-100": isMobile,
          })}
        />
      </Layout>

      <Drawer
        title={
          <span className="lmf__drawer--title">
            <img src={relatedPersonIcon} alt="" />
            <span>{TRANSLATION.PRODUCTS.ADD_BENEFICIARY}</span>
          </span>
        }
        placement="bottom"
        className="lmf__drawer"
        closable
        onClose={() => setDrawerVisible(false)}
        open={drawerVisible}
        height="85vh"
      >
        <div className="lmfcontent__related_persons">
          {(childrenNumber > 0 || addBeneficiaries.spouse) && (
            <div className="beneficiary_chips">
              {addBeneficiaries.spouse &&
                get(values, "spouse.identity.user.birthDate") && (
                  <div className="beneficiary_chips--button">
                    <span>{TRANSLATION.PRODUCTS.SPOUSE}</span>
                    <RxCross2
                      size={14}
                      onClick={() => {
                        formProps.setFieldValue("spouse", undefined);
                        setAddBeneficiaries((prev) => ({
                          ...prev,
                          spouse: false,
                        }));
                        handleSubmit();
                      }}
                    />
                  </div>
                )}
              {get(values, "children", [])
                .filter((child) => get(child, "identity.user.birthDate"))
                .map((_, i) => (
                  <div key={`child_${i}`} className="beneficiary_chips--button">
                    <span>{`${TRANSLATION.CHILDREN.CHILD.TITLE} ${
                      i + 1
                    }`}</span>
                    <RxCross2
                      onClick={() => {
                        values.children.splice(i, 1);
                        setChildrenNumber((prev) => +prev - 1);
                        if (childrenNumber === 1)
                          setAddBeneficiaries((prev) => ({
                            ...prev,
                            children: false,
                          }));
                        formProps.setFieldValue(
                          "children",
                          get(values, "children", []),
                        );
                        handleSubmit();
                      }}
                      size={14}
                    />
                  </div>
                ))}
            </div>
          )}
          <div className="beneficiary__lmf">
            <div className="beneficiary__lmf--expand">
              <InfoBox
                variant="warning"
                text={TRANSLATION_2.INFO_WARNING}
                icon={<Help size={48} />}
              />

              <div className="beneficiary__lmf--expand--row spouse">
                <Input
                  type="checkbox"
                  label={TRANSLATION.PRODUCTS.SPOUSE}
                  checked={addBeneficiaries.spouse}
                  onChange={({ target: { checked } }) => {
                    setAddBeneficiaries((prev) => ({
                      ...prev,
                      spouse: checked,
                    }));
                    if (checked)
                      formProps.setFieldValue("spouse", {
                        id: cuid(),
                        relationship: RELATIONSHIP.SPOUSE,
                        identity: {
                          id: cuid(),
                          user: {
                            id: cuid(),
                            birthDate: null,
                          },
                        },
                      });
                    else formProps.setFieldValue("spouse", undefined);
                  }}
                />
                <div className="field">
                  <Input
                    type="date"
                    placeholder={TRANSLATION.BIRTH_DATE.LABEL}
                    format={DATE_FORMAT}
                    disabled={!addBeneficiaries.spouse}
                    showToday={false}
                    {...bindDateInputProps({
                      name: "spouse.identity.user.birthDate",
                      values,
                      ...formProps,
                    })}
                    defaultPickerValue={
                      !get(values, `spouse.identity.user.birthDate`) &&
                      dayjs().subtract(24, "years")
                    }
                    disabledDate={(current) =>
                      current > dayjs().subtract(18, "years").endOf("year") ||
                      current < hundredYearsAgoStart
                    }
                  />
                </div>
              </div>
              <div className="divider" />
              <div className="beneficiary__lmf--expand--row">
                <div className="beneficiary__lmf--expand--row__children">
                  <Input
                    type="checkbox"
                    label={TRANSLATION.PRODUCTS.CHILDREN}
                    checked={addBeneficiaries.children}
                    onChange={({ target: { checked } }) => {
                      if (!checked) {
                        setChildrenNumber(0);
                        formProps.setFieldValue("children", []);
                      } else if (childrenNumber === 0) setChildrenNumber(1);
                      setAddBeneficiaries((prev) => ({
                        ...prev,
                        children: checked,
                      }));
                    }}
                  />
                  <p>Gratuité à partir du 3ème enfant </p>
                </div>
                <div className="children-count">
                  <Button
                    className="button-small-rounded"
                    onClick={() => setChildrenNumber((prev) => +prev - 1)}
                    disabled={childrenNumber < 2 || !addBeneficiaries.children}
                  >
                    <FaMinus size={14} />
                  </Button>
                  <div className="flex--grow-1 counter">
                    <Input
                      type="number"
                      min={0}
                      max={12}
                      placeholder={"0"}
                      name={"gender"}
                      value={childrenNumber}
                      onChange={({ target: { value } }) => {
                        setChildrenNumber(+value);
                      }}
                      disabled={!addBeneficiaries.children}
                    />
                  </div>
                  <Button
                    className="button-small-rounded"
                    onClick={() => setChildrenNumber((prev) => +prev + 1)}
                    disabled={childrenNumber > 11 || !addBeneficiaries.children}
                  >
                    <FaPlus size={14} />
                  </Button>
                </div>
              </div>
              <div className="children-birthday">
                {childrenNumber > 0 && (
                  <InfoBox
                    variant="info"
                    text={TRANSLATION_2.INFO_INFO}
                    icon={<HelpBlue size={40} />}
                    onClick={() => setOpenInfoModal(true)}
                  />
                )}

                {Array(childrenNumber)
                  .fill()
                  .map((_, i) => (
                    <Input
                      direction="row"
                      key={`child-${i}`}
                      label={`${TRANSLATION.NUMBERS_MAPPING[i + 1] || i + 1} ${
                        TRANSLATION.CHILDREN_BIRTHDATES.CHILD
                      }  (-28 ans)`}
                      placeholder={TRANSLATION.CHILDREN_BIRTHDATES.BIRTH_DATE}
                      type="date"
                      variant="simple"
                      format={DATE_FORMAT}
                      disabledDate={(current) => {
                        return (
                          current > dayjs() ||
                          current < dayjs().subtract(28, "year").startOf("year")
                        );
                      }}
                      defaultPickerValue={
                        !get(values, `children.${i}.identity.user.birthDate`) &&
                        dayjs().subtract(1, "years")
                      }
                      showToday={false}
                      {...bindDateInputProps({
                        name: `children.${i}.identity.user.birthDate`,
                        values,
                        ...formProps,
                      })}
                    />
                  ))}
              </div>
              <Button
                type="white"
                size="middle"
                className="w-full"
                onClick={() => {
                  handleSubmit();
                  setDrawerVisible(false);
                }}
                disabled={
                  !isEmpty(formProps.errors) ||
                  (childrenNumber === 0 && !addBeneficiaries.spouse)
                }
              >
                {TRANSLATION.PRODUCTS.SUBMIT}
              </Button>
            </div>
          </div>
        </div>
      </Drawer>
    </>
  );
};

export default View;
